import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    NgZone,
    OnInit,
    ViewEncapsulation,
    inject,
    input,
    signal,
} from '@angular/core';
import { EventBusService } from 'apps/account/src/account-shared/services/event-bus.service';
import { PhotoContestApiService } from 'apps/account/src/account-shared/services/photo-contest-api.service';
import { IncentivesV1ApiService } from 'apps/account/src/account-shared/services/incentives-v1-api.service';
import { ImageV1ApiService } from 'apps/account/src/account-shared/services/image-v1-api.service';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { IdentityService, SessionService } from 'account-hybrid/core/authentication';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { TranslateService } from '@ngx-translate/core';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { buildCnameUrl } from 'apps/account/src/account-shared/pipes/cname-url.util';
import { EditorsModule } from '../../../../account-shared/forms/editors/editors.module';
import { ShortUrlsModule } from '../../../downgraded/short-urls/short-urls.module';
import { CurationBackgroundImageDirective } from '../../stories/curation-card/background-image.directive';
import { TranslateModule } from '@ngx-translate/core';
import { CnameUrlPipe, OnOffPipe } from './contest-sheets.pipes';
import { contestDisplayHtml } from './contest-html.util';
import { FtIframeContentComponent } from './ft-iframe-content.component';
import { FtLanguageSelectorComponent } from './ft-language-selector.component';
import { FieldEditorModalConfig, FtFieldEditorModalComponent } from './field-editor-modal.component';
import { computeDiffs, revertDiffs, snapshotClone } from './native-diff.util';
import { FtImageLibraryComponent } from '../touchpoint-sheets/ft-image-library.component';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtModalChromeComponent } from '../../../common/components/modal/ft-modal-chrome.component';
import { InvitationShareModalService } from './invitation-share/invitation-share-modal.service';
import { FtDisabledDirective } from '../touchpoint-sheets/ft-disabled.directive';

/**
 * Contest INVITATIONS sheet: the read-view (share preview, links, permalink,
 * short-url list, config summaries) plus native edit modals — field editors via
 * FtFieldEditorModalComponent, the background-image picker, and the social share
 * buttons via InvitationShareModalService. `save()` PUTs the full contest union
 * via PhotoContestApiService (omitted fields would be wiped server-side).
 */
@Component({
    selector: 'ft-contest-invitations-ng',
    imports: [
        EditorsModule,
        ShortUrlsModule,
        CurationBackgroundImageDirective,
        TranslateModule,
        OnOffPipe,
        CnameUrlPipe,
        FtIframeContentComponent,
        FtLanguageSelectorComponent,
        FtFieldEditorModalComponent,
        FtImageLibraryComponent,
        FtModalComponent,
        FtModalChromeComponent,
        FtDisabledDirective,
    ],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './contest-invitations.component.html',
    styleUrls: ['./contest-invitations.component.scss'],
})
export class FtContestInvitationsComponent implements OnInit {
    readonly contestUuid = input.required<string>();

    /** Native field-editor modal request (bool fields). `null` = closed. */
    protected readonly fieldEditorConfig = signal<FieldEditorModalConfig | null>(null);
    /** ftModal phase 2: open-time deep clone of languages.item for the native html field cancel-rollback. */
    private languagesSnapshot: any[] | null = null;

    protected readonly data = signal<any>({});
    protected readonly model = signal<any>({
        language: null,
        invitationUrl: null,
        shareImagePreviewTemplate: null,
        networks: {
            fb: { title: 'facebook', socialNetworkType: 1 },
            tw: { title: 'twitter', socialNetworkType: 2 },
            li: { title: 'linkedin', socialNetworkType: 3 },
        },
        allowedPlacesTypes: [{ value: 0, label: 'All' }],
        // v1 $watch'ed data.contest.isInviteeRequestPlaceForPhotoEnabled and rebuilt this array IN
        // PLACE, so the "Required" row appeared live once "Status" was switched on inside the open
        // editor (details.controller.js:65-80). Natively the row declares `showWhen` and
        // ft-bool-field gates it off the live datasource — same rendered result, no watch needed.
        fieldsForPlacePhoto: [
            { name: 'isInviteeRequestPlaceForPhotoEnabled', label: 'Status' },
            { name: 'isInviteeRequestPlaceForPhotoRequired', label: 'Required', showWhen: 'isInviteeRequestPlaceForPhotoEnabled' },
        ],
        placesCount: [],
        propertyId: undefined,
        defaultBackgroundImageUrl: undefined,
    });
    protected readonly isFliptoStaff = signal(false);
    protected noneSetText = '';

    /** WS5 Phase 3 #7a: native invitee background-image modal open state (own CDK overlay). */
    protected readonly inviteeImageModalOpen = signal(false);

    private readonly photoContest: any = inject(PhotoContestApiService);
    private readonly incentives: any = inject(IncentivesV1ApiService);
    private readonly imageApi: any = inject(ImageV1ApiService);
    private readonly spaces: any = inject(SpacesService);
    private readonly session: any = inject(SessionService);
    private readonly identity: any = inject(IdentityService);
    private readonly invitationShareModal: InvitationShareModalService = inject(InvitationShareModalService);
    private readonly parse: any = inject(ParseService);
    private readonly translate: (key: string) => string;
    private readonly eventBus = inject(EventBusService);
    private readonly zone = inject(NgZone);
    private readonly environment = inject<Environment>(ENVIRONMENT_TOKEN);

    constructor() {
        const translateSvc = inject(TranslateService);
        this.translate = (key: string, params?: any) => translateSvc.instant(key, params);
        this.noneSetText = this.translate('flipto.account.NoneSet');
    }

    ngOnInit(): void {
        this.isFliptoStaff.set(this.identity.isFliptoStaff());
        const property = this.session.getProperty(this.spaces.getPropertyUuid());
        this.model().propertyId = this.spaces.getPropertyUuid();
        this.model().defaultBackgroundImageUrl = property.BackgroundImageUrl;
        this.loadContest();
    }

    protected get selectedAllowedPlaces(): any[] {
        const value = this.data()?.contest?.allowedPlacesType;
        return (this.model().allowedPlacesTypes || []).filter((o: any) => o.value == value);
    }

    /** The read-side sink — see {@link contestDisplayHtml} for why it stays an unsanitised string. */
    protected readonly displayHtml = contestDisplayHtml;

    // --- edit-modal bridges (hidden, click-only — verified by live diag) ---

    // ftModal phase 2 #9: the invitation message html (WYSIWYG) editors → native FtHtmlField on the
    // CDK shell (full-PUT save(); cancel reverts the nested language edit via the snapshot).
    protected editInviteeGreeting(): void {
        this.languagesSnapshot = snapshotClone(this.data().contest.languages.item);
        this.fieldEditorConfig.set({
            type: 'html',
            datasource: this.data().contest.languages.item,
            fields: [{ name: 'inviteeGreeting' }],
            fieldsOptions: { tokens: ['first-prize', 'property'] },
            title: this.translate('flipto.account.sections.platform.photoAdvocacy.InvitationGreetingMessage'),
            onDone: () => this.save(),
            onCancel: () => this.rollbackLanguages(),
            onClose: () => {
                this.languagesSnapshot = null;
            },
        });
    }

    /** Cancel: revert the native html field's in-place edits (v1 tracker.rollback → DeepDiff.revertChange). */
    private rollbackLanguages(): void {
        if (!this.languagesSnapshot) return;
        revertDiffs(this.data().contest.languages.item, this.languagesSnapshot, computeDiffs(this.languagesSnapshot, this.data().contest.languages.item));
    }

    // ftModal phase 2: bool field editors open the NATIVE modal shell (CDK overlay → above the
    // CDK contest sheet, fixing the bridged-ftModal occlusion). datasource = live `data.contest`;
    // Done → save() (the FULL union PUT), Cancel → snapshot rollback.

    protected editRequestPlaceInfo(): void {
        this.fieldEditorConfig.set({
            type: 'bool',
            datasource: this.data().contest,
            fields: this.model().fieldsForPlacePhoto,
            title: this.translate('flipto.account.sections.platform.photoAdvocacy.RequestPlaceInfoForPhoto'),
            description: 'Ask your storytellers where their photo was taken.',
            onDone: () => this.save(),
        });
    }

    protected editPlaceMessage(): void {
        this.languagesSnapshot = snapshotClone(this.data().contest.languages.item);
        this.fieldEditorConfig.set({
            type: 'html',
            datasource: this.data().contest.languages.item,
            fields: [{ name: 'inviteeQuotePlaceRequestMessage' }],
            title: 'Place message',
            onDone: () => this.save(),
            onCancel: () => this.rollbackLanguages(),
            onClose: () => {
                this.languagesSnapshot = null;
            },
        });
    }

    protected editRequestQuotes(): void {
        this.fieldEditorConfig.set({
            type: 'bool',
            datasource: this.data().contest,
            fields: [{ name: 'isInviteeRequestQuotes', label: 'Status' }],
            title: this.translate('flipto.account.sections.platform.photoAdvocacy.RequestQuotes'),
            description: this.translate('flipto.account.sections.platform.photoAdvocacy.RequestQuotesDescription'),
            onDone: () => this.save(),
        });
    }

    protected editInviteeQuoteRequest(): void {
        this.languagesSnapshot = snapshotClone(this.data().contest.languages.item);
        this.fieldEditorConfig.set({
            type: 'html',
            datasource: this.data().contest.languages.item,
            fields: [{ name: 'inviteeQuoteRequestMessage' }],
            title: this.translate('flipto.account.sections.platform.photoAdvocacy.InvitationQuoteRequestMessage'),
            onDone: () => this.save(),
            onCancel: () => this.rollbackLanguages(),
            onClose: () => {
                this.languagesSnapshot = null;
            },
        });
    }

    protected onAllowedPlacesSave(value: any): void {
        this.data().contest.allowedPlacesType = value;
        this.save();
    }

    /** Opens the native invitee background-image library modal (own CDK overlay → above the sheet). */
    protected openInviteeImageLibrary(): void {
        this.inviteeImageModalOpen.set(true);
    }

    /** Chrome `(done)` — v1 ftModal on-done="save" (the full-union PUT) then close. */
    protected onInviteeImageDone(): void {
        this.save();
        this.inviteeImageModalOpen.set(false);
    }

    protected updateInviteeImage(image: any): void {
        this.data().contest.inviteeBackgroundImageUrl = image ? image.url : null;
    }

    protected shareInvitation(network: any): void {
        const params: any = { contest: this.data().contest.slug, source: network.title };
        if (this.model().language.code !== 'en') {
            params.lang = this.model().language.code;
        }
        this.invitationShareModal.show(
            this.data().contest.id,
            this.data().contest.companyCode,
            this.model().language.contestName,
            this.data().contest.inviteeBackgroundImageUrl,
            buildCnameUrl(this.data().contest.subdomains, 3, this.data().contest.propertySlug, params, this.environment.FliptoHubDomain),
            this.session.getProperty(this.spaces.getPropertyUuid()),
            network,
            this.model().language.code,
        );
    }

    // --- save (the FULL union the v1 controller PUTs) ---

    save(): void {
        const propertyUuid = this.spaces.getPropertyUuid();
        const model = {
            languages: (this.data().contest.languages.item || []).map((language: any) => ({
                languageCode: language.code,
                followerMessage: language.followerMessage,
                guestMessage: language.guestMessage,
                contestTerms: language.terms,
                prizesTerms: this.setTargetBlankToLinks(language.prizesTerms),
                releaseTerms: this.setTargetBlankToLinks(language.releaseTerms),
                submitConsent: this.setTargetBlankToLinks(language.submitConsent),
                phoneConsent: this.setTargetBlankToLinks(language.phoneConsent),
                firstPrize: language.firstPrize,
                secondPrize: language.secondPrize,
                inviteeGreeting: language.inviteeGreeting,
                inviteeQuoteRequestMessage: language.inviteeQuoteRequestMessage,
                inviteeQuotePlaceRequestMessage: language.inviteeQuotePlaceRequestMessage,
            })),
            isAutoInvite: this.data().contest.isAutoInvite,
            isPhoneConsentFeatureEnabled: this.parseIsPhoneConsentFeatureEnabled(this.data().contest.isPhoneConsentFeatureEnabled),
            phoneConsentAcceptanceModeId: this.data().contest.phoneConsentAcceptanceModeId,
            isPrivate: this.data().contest.isPrivate,
            isExactPlaceRanking: this.data().contest.isExactPlaceRanking,
            isInviteeRequestPlaceForPhotoEnabled: this.data().contest.isInviteeRequestPlaceForPhotoEnabled,
            isInviteeRequestPlaceForPhotoRequired: this.data().contest.isInviteeRequestPlaceForPhotoRequired,
            allowedPlacesType: this.data().contest.allowedPlacesType,
            isInviteeRequestQuotes: this.data().contest.isInviteeRequestQuotes,
            contestCss: this.data().contest.contestCss,
            inviteeBackgroundImageUrl: this.data().contest.inviteeBackgroundImageUrl,
            theme: this.data().contest.themeId,
            inviteePhoneDisplayModeId: this.data().contest.inviteePhoneDisplayModeId,
            friendPhoneDisplayModeId: this.data().contest.friendPhoneDisplayModeId,
        };

        Promise.resolve(this.photoContest.update(propertyUuid, this.contestUuid(), model))
            .then(
                () => this.loadContest(),
                () => this.loadContest(),
            )
            .then(() => {
                // WS5 #10 inc 2i: native↔native (listener = the contest-container header) → the bus.
                this.eventBus.emit('flipto.photo-contest-changed', this.data().contest);
            });
    }

    private parseIsPhoneConsentFeatureEnabled(value: any): boolean {
        return typeof value === 'boolean' ? value : Boolean(parseInt(value, 10));
    }

    private setTargetBlankToLinks(html: string): string {
        if (!html) {
            return html;
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const body = doc.querySelector('body')!;
        body.querySelectorAll('a').forEach((link) => {
            if (!link.hasAttribute('target') || link.getAttribute('target') !== '_blank') {
                link.setAttribute('target', '_blank');
            }
        });
        return body.innerHTML.replace(/&nbsp;/g, ' ');
    }

    private loadContest(): Promise<void> {
        const propertyUuid = this.spaces.getPropertyUuid();
        return Promise.all([
            Promise.resolve(this.photoContest.load({ contestId: this.contestUuid(), propertyUuid })),
            Promise.resolve(this.incentives.getAvailableIncentives(propertyUuid)),
            Promise.resolve(this.imageApi.getPropertyImages({ propertyId: propertyUuid })),
        ]).then((args: any[]) => {
            const contestDetails = args[0];
            const propertyIncentives = args[1].data;
            const propertyImages = args[2]['asset_images'] ? args[2]['asset_images'] : [];

            // The invitations intro iframe preview (Facebook share-image template).
            Promise.resolve(
                this.photoContest.getInvitationShareImageTemplate(propertyUuid, {
                    SocialApplication: this.parse.int(this.model().networks.fb.socialNetworkType),
                    ContestUUID: this.contestUuid(),
                    LanguageCode: 'en',
                    propertyUuid,
                }),
            ).then((data: any) => {
                this.zone.run(() => {
                    this.model().shareImagePreviewTemplate = data.template;
                });
            });

            this.zone.run(() => {
                const contest = JSON.parse(JSON.stringify(contestDetails));
                this.data.set({
                    contest,
                    originalGoals: JSON.parse(JSON.stringify(contestDetails.goals ? contestDetails.goals.item : [])),
                    propertyIncentives,
                    propertyImages,
                });

                contest.subdomains = this.parse.arrayOrDefault(contest.subdomains);
                contest.isAutoInvite = this.parse.bool(contest.isAutoInvite);
                contest.isPrivate = this.parse.bool(contest.isPrivate);
                contest.isExactPlaceRanking = this.parse.bool(contest.isExactPlaceRanking);
                contest.isInviteeRequestPlaceForPhotoEnabled = this.parse.bool(contest.isInviteeRequestPlaceForPhotoEnabled);
                contest.isInviteeRequestPlaceForPhotoRequired = this.parse.bool(contest.isInviteeRequestPlaceForPhotoRequired);
                contest.allowedPlacesType = this.parse.int(contest.allowedPlacesType);
                contest.isInviteeRequestQuotes = this.parse.bool(contest.isInviteeRequestQuotes);

                const count = this.parse.intOrDefault(contest.placesCount);
                this.model().allowedPlacesTypes = [{ value: 0, label: 'All' }];
                if (count > 0) {
                    this.model().allowedPlacesTypes = [...this.model().allowedPlacesTypes, { value: 1, label: 'Your places' + ' (' + count + ')' }];
                }

                // v1 _.find(contest.languages.item, { code: 'en' }) — languages.item is always an
                // array here (PhotoContestApiService.load → sortByDefaultLanguage normalizes it).
                this.model().language = (contest.languages.item || []).find((l: any) => l.code === 'en');
                this.model().invitationUrl = buildCnameUrl(contest.subdomains, 3, contest.propertySlug, undefined, this.environment.FliptoHubDomain);
            });
        });
    }
}
